import { supabase } from "@/integrations/supabase/client";

export type PlanType = "trial" | "basic" | "professional" | "professional_plus" | "professional_enterprise";

export interface PlanInfo {
  type: PlanType;
  isPremium: boolean;
  canUseNF: boolean;
  canAddOperators: boolean;
  maxOperators: number;
  planName?: string;
}

/**
 * Detecta o plano atual da empresa
 */
export async function getPlanInfo(empresaId: string): Promise<PlanInfo> {
  try {
    // Buscar dados da empresa
    const { data: empresa, error: empresaError } = await supabase
      .from("empresas")
      .select("status, is_premium")
      .eq("id", empresaId)
      .single();

    console.log("🔍 getPlanInfo - empresa:", { empresaId, empresa, empresaError });

    if (empresaError) {
      console.error("Erro ao buscar empresa:", empresaError);
      return getDefaultPlanInfo("trial");
    }

    // Se está em trial
    if (empresa.status === "trial") {
      console.log("✅ getPlanInfo - Plano TRIAL detectado");
      return getDefaultPlanInfo("trial");
    }

    // Verificar se tem assinatura profissional (via Asaas)
    if (empresa.is_premium) {
      const { data: subscriptions, error: subError } = await supabase
        .from("asaas_subscriptions")
        .select("plan_type, status")
        .eq("empresa_id", empresaId)
        .eq("status", "ACTIVE");

      console.log("🔍 getPlanInfo - subscriptions:", { subscriptions, subError });

      const subscription = Array.isArray(subscriptions) ? subscriptions[0] : null;

      if (subscription?.plan_type) {
        // Mapear o plan_type da assinatura para nossos tipos internos
        if (subscription.plan_type.includes("enterprise")) {
          console.log("✅ getPlanInfo - Plano ENTERPRISE detectado");
          return getDefaultPlanInfo("professional_enterprise");
        } else if (subscription.plan_type.includes("plus")) {
          console.log("✅ getPlanInfo - Plano PLUS detectado");
          return getDefaultPlanInfo("professional_plus");
        } else if (subscription.plan_type.includes("professional")) {
          console.log("✅ getPlanInfo - Plano PROFESSIONAL detectado");
          return getDefaultPlanInfo("professional");
        }
      } else {
        // is_premium=true mas sem subscription ativa no Asaas
        // Assumir que foi ativado manualmente no Supabase como professional
        console.log("✅ getPlanInfo - is_premium=true (sem assinatura Asaas), assumindo PROFESSIONAL");
        return getDefaultPlanInfo("professional");
      }
    }

    // Plano básico
    console.log("✅ getPlanInfo - Plano BÁSICO detectado");
    return getDefaultPlanInfo("basic");
  } catch (error) {
    console.error("Erro ao obter informações do plano:", error);
    return getDefaultPlanInfo("trial");
  }
}

/**
 * Retorna as características padrão de cada plano
 */
function getDefaultPlanInfo(planType: PlanType): PlanInfo {
  const planConfigs: Record<PlanType, PlanInfo> = {
    trial: {
      type: "trial",
      isPremium: false,
      canUseNF: false,
      canAddOperators: false,
      maxOperators: 1,
      planName: "Trial",
    },
    basic: {
      type: "basic",
      isPremium: false,
      canUseNF: false,
      canAddOperators: false,
      maxOperators: 1,
      planName: "Básico",
    },
    professional: {
      type: "professional",
      isPremium: true,
      canUseNF: true,
      canAddOperators: true,
      maxOperators: 3,
      planName: "Profissional",
    },
    professional_plus: {
      type: "professional_plus",
      isPremium: true,
      canUseNF: true,
      canAddOperators: true,
      maxOperators: 5,
      planName: "Profissional Plus",
    },
    professional_enterprise: {
      type: "professional_enterprise",
      isPremium: true,
      canUseNF: true,
      canAddOperators: true,
      maxOperators: 5,
      planName: "Profissional Enterprise",
    },
  };

  return planConfigs[planType];
}

/**
 * Verifica se pode usar uma funcionalidade específica
 */
export async function canUseFeature(
  empresaId: string,
  feature: "nf" | "operators"
): Promise<boolean> {
  const planInfo = await getPlanInfo(empresaId);

  switch (feature) {
    case "nf":
      return planInfo.canUseNF;
    case "operators":
      return planInfo.canAddOperators;
    default:
      return false;
  }
}
